import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout, Typography, Input, Button, Tooltip, Avatar, Empty,
  Spin, Alert, Popconfirm, Tree, Tag, Select, message,
} from 'antd';
import {
  UserOutlined, LogoutOutlined, LockOutlined, UnlockOutlined,
  SaveOutlined, PlusOutlined, DeleteOutlined,
} from '@ant-design/icons';
import yaml from 'js-yaml';
import EnvVersionLabel from './Envversionlabel';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

// public/rules/focused_conditions.yaml is served at /rules/focused_conditions.yaml
const FOCUSED_CONDITIONS_URL = '/rules/focused_conditions.yaml';

const EDITABLE_ROLES = ['hcc_reviewer'];



// ── YAML comment-group parsing ───────────────────────────────────────
// The YAML file delineates logical sub-groups with 3-line comment blocks:
//   # ════════════════════════════════════════════════════════════
//   # Endocrine — Thyroid
//   # ════════════════════════════════════════════════════════════
// We walk the raw text to find these (so we can group entries by their
// nearest preceding header), since js-yaml strips comments on parse.
function extractGroupHeaders(text) {
  const lines = text.split('\n');
  const headers = []; // { title, lineNum }
  for (let i = 0; i < lines.length - 2; i++) {
    if (/^\s*#\s*═+\s*$/.test(lines[i]) &&
        /^\s*#\s*═+\s*$/.test(lines[i + 2])) {
      const m = lines[i + 1].match(/^\s*#\s+(.+?)\s*$/);
      if (m) headers.push({ title: m[1].trim(), lineNum: i });
    }
  }
  return headers;
}

// Returns the start-line for each entry (each entry begins with "- terms:").
// js-yaml preserves array order, so by-index matching reliably aligns the
// parsed entries to the raw-text positions.
function extractEntryStartLines(text) {
  const lines = text.split('\n');
  const starts = [];
  lines.forEach((line, idx) => {
    if (/^\s+-\s+terms:/.test(line)) starts.push(idx);
  });
  return starts;
}

// Build Ant Tree data from a flat ordered list of group titles. Titles
// containing " — " split into parent ("Endocrine") and child ("Thyroid");
// titles without it become top-level leaves ("Lipid disorders", "GI").
function buildTreeData(groupTitles) {
  const seenParents = new Map();   // parent -> { kind: 'group', children: [] }
  const seenLeaves  = new Set();   // top-level leaf keys
  const order = [];                // preserve discovery order

  for (const fullTitle of groupTitles) {
    const parts = fullTitle.split(/\s+—\s+/);

    if (parts.length === 2) {
      const [parent, child] = parts;
      if (!seenParents.has(parent)) {
        seenParents.set(parent, { children: [] });
        order.push({ kind: 'group', name: parent });
      }
      seenParents.get(parent).children.push({
        title: child,
        key: fullTitle,
        isLeaf: true,
      });
    } else {
      if (!seenLeaves.has(fullTitle)) {
        seenLeaves.add(fullTitle);
        order.push({ kind: 'leaf', name: fullTitle });
      }
    }
  }

  return order.map(item => {
    if (item.kind === 'leaf') {
      return { title: item.name, key: item.name, isLeaf: true };
    }
    return {
      title: item.name,
      key: `__parent::${item.name}`,
      selectable: false,
      children: seenParents.get(item.name).children,
    };
  });
}

/**
 * FocusedConditionsConfig
 *
 * Tree-on-left, entries-on-right editor for focused_conditions.yaml.
 *
 * Shell mirrors SectionDefinitionsEditor / WorkQueuePage. Breadcrumb is
 * "HCC STUDIO / Focused Conditions", back-link returns to
 * /hcc-config. Lock and Save sit top-right (Save is placeholder, no
 * persistence yet).
 *
 * Left: Ant Tree built from the YAML's "═══" comment-block titles. Titles
 * shaped "Parent — Child" collapse into parent groups (Endocrine →
 * Thyroid/Adrenal, Cardiovascular → Hypertension/...); flat titles
 * (Lipid disorders, GI, Vascular) sit at the top level. Only leaves are
 * selectable.
 *
 * Right: list of entries belonging to the selected group. Each entry shows
 * code (monospace, prominent), category (small Tag), description (text),
 * and terms (pill chips). In edit mode the fields become Inputs/TextArea
 * and terms use a Select tags input; the Add Entry button at the top
 * appends a blank card to the selected group.
 *
 * Data:
 *   - Fetched from /rules/focused_conditions.yaml (public/rules).
 *   - Parsed with js-yaml; comment headers extracted in a separate text
 *     walk. Entry-to-group association is by line-number proximity.
 *   - All edits are in-memory. No backend.
 *
 * Dependency: js-yaml (`npm install js-yaml`).
 */
const FocusedConditionsConfig = ({
  username,
  sessionId,
  userRole,
  userFeatures,
  tabOrder,
  onLogout,
}) => {
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────────────
  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [groupTitles, setGroupTitles] = useState([]);   // string[] in source order
  const [entries, setEntries]     = useState([]);       // { _id, _groupTitle, terms, category, code, description }[]
  const [selectedGroupTitle, setSelectedGroupTitle] = useState(null);
  const [editMode, setEditMode]   = useState(false);    // false = locked

  const canEdit = EDITABLE_ROLES.includes(userRole);


  // Stable id generator for newly added entries
  const [nextNewId, setNextNewId] = useState(0);

  // ── Load + parse ─────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    fetch(FOCUSED_CONDITIONS_URL)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(text => {
        if (cancelled) return;

        // 1) Pull comment-bracketed group headers from raw text.
        const headers = extractGroupHeaders(text);

        // 2) Find the line where each YAML entry begins.
        const entryStarts = extractEntryStartLines(text);

        // 3) Parse the structured YAML for entry contents.
        let parsed;
        try {
          parsed = yaml.load(text);
        } catch (err) {
          setLoadError(`YAML parse error: ${err.message}`);
          return;
        }
        const rawEntries = (parsed && Array.isArray(parsed.conditions))
          ? parsed.conditions
          : [];

        // 4) Associate each entry with its nearest preceding group header.
        const enriched = rawEntries.map((entry, idx) => {
          const entryLine = entryStarts[idx] ?? Infinity;
          let groupTitle = '(Ungrouped)';
          for (let g = headers.length - 1; g >= 0; g--) {
            if (headers[g].lineNum < entryLine) {
              groupTitle = headers[g].title;
              break;
            }
          }
          return {
            _id: `seed-${idx}`,
            _groupTitle: groupTitle,
            terms: Array.isArray(entry.terms) ? entry.terms : [],
            category: entry.category || '',
            code: entry.code || '',
            description: entry.description || '',
          };
        });

        setGroupTitles(headers.map(h => h.title));
        setEntries(enriched);
        if (headers.length > 0) {
          setSelectedGroupTitle(headers[0].title);  // default to first leaf
        }
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load focused_conditions.yaml:', err);
        setLoadError(err.message || 'Unknown error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // ── Derived: tree data + filtered entries ────────────────────────
  const treeData = useMemo(() => buildTreeData(groupTitles), [groupTitles]);

  // Expand all parent nodes by default.
  const defaultExpandedKeys = useMemo(
    () => treeData.filter(n => !n.isLeaf).map(n => n.key),
    [treeData]
  );

  const entriesForSelectedGroup = useMemo(() => {
    if (!selectedGroupTitle) return [];
    return entries.filter(e => e._groupTitle === selectedGroupTitle);
  }, [entries, selectedGroupTitle]);

  // Unique category values across the whole dataset — fed to the per-entry
  // category Select as autocomplete options.
  const knownCategories = useMemo(() => {
    const s = new Set();
    entries.forEach(e => { if (e.category) s.add(e.category); });
    return Array.from(s).sort();
  }, [entries]);

  // ── Handlers ─────────────────────────────────────────────────────
  const handleLockToggle = () => setEditMode(prev => !prev);

  const handleSave = () => {
    message.info('Save is not yet wired up — edits are in memory only.');
  };

  const handleTreeSelect = (selectedKeys) => {
    if (!selectedKeys || selectedKeys.length === 0) return;
    const key = selectedKeys[0];
    if (typeof key === 'string' && !key.startsWith('__parent::')) {
      setSelectedGroupTitle(key);
    }
  };

  const updateEntry = (id, patch) => {
    setEntries(prev => prev.map(e => (e._id === id ? { ...e, ...patch } : e)));
  };

  const handleDeleteEntry = (id) => {
    setEntries(prev => prev.filter(e => e._id !== id));
  };

  const handleAddEntry = () => {
    if (!selectedGroupTitle) return;
    const id = `new-${nextNewId}`;
    setNextNewId(n => n + 1);
    setEntries(prev => [
      ...prev,
      {
        _id: id,
        _groupTitle: selectedGroupTitle,
        terms: [],
        category: '',
        code: '',
        description: '',
      },
    ]);
  };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Sider ──────────────────────────────────────────────── */}
      <Sider
        width={60}
        collapsible
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'fixed',
          left: 0, top: 0, bottom: 0,
          zIndex: 10,
        }}
      >
        <div style={{
          display: 'flex',
          background: 'linear-gradient(180deg, #162349 0%, #2a4a7c 100%)',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          alignItems: 'center',
          padding: '20px 0',
        }}>
          <div></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
            <Tooltip title={username} placement="right">
              <Avatar
                style={{ backgroundColor: 'white', color: '#162349', cursor: 'pointer', border: '2px solid white' }}
                icon={<UserOutlined />}
              />
            </Tooltip>
            <Tooltip title="Logout" placement="right">
              <Button
                type="text"
                icon={<LogoutOutlined style={{ color: 'white' }} />}
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>
            <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 'Bold' }} />
          </div>
        </div>
      </Sider>

      <Layout style={{ marginLeft: 60 }}>

        {/* ── Header ──────────────────────────────────────────── */}
        <Header style={{
          background: '#162349',
          padding: '0 20px',
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
            <span
              onClick={() => navigate('/hcc-config')}
              style={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: 20, fontWeight: 700,
                cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = 'white')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
            >
              HCC STUDIO
            </span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }}>/</span>
            <span style={{
              color: 'white',
              fontSize: 20, fontWeight: 700,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              Focused Conditions
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {canEdit ? (
              <>
                <Tooltip title={editMode ? 'Click to lock (return to view-only)' : 'Click to unlock for editing'}>
                  <Button
                    icon={editMode ? <UnlockOutlined /> : <LockOutlined />}
                    onClick={handleLockToggle}
                    style={{
                      backgroundColor: editMode ? '#FEF3C7' : 'white',
                      color: '#162349',
                      fontWeight: 600,
                      borderColor: editMode ? '#F59E0B' : '#d9d9d9',
                    }}
                  >
                    {editMode ? 'Unlocked' : 'Locked'}
                  </Button>
                </Tooltip>
                <Tooltip title={editMode ? 'Save (not yet wired up)' : 'Unlock to enable saving'}>
                  <Button
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    disabled={!editMode}
                    style={{ backgroundColor: 'white', color: '#162349', fontWeight: 600 }}
                  >
                    Save
                  </Button>
                </Tooltip>
              </>
            ) : (
              <Tag
                style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontWeight: 600,
                  fontSize: 13,
                  padding: '4px 12px',
                }}
              >
                Display Only
              </Tag>
            )}
          </div>

        </Header>

        {/* ── Content ─────────────────────────────────────────── */}
        <Content style={{
          padding: '28px 32px',
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)',
          minHeight: 'calc(100vh - 64px)',
        }}>

          {loading && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#6B7280', fontSize: 14 }}>
                Loading focused conditions…
              </div>
            </div>
          )}

          {!loading && loadError && (
            <Alert
              type="warning"
              showIcon
              message="Configuration file not found"
              description={
                <span>
                  Could not load <code>{FOCUSED_CONDITIONS_URL}</code>. ({loadError})
                </span>
              }
              style={{ maxWidth: 640, margin: '40px auto' }}
            />
          )}

          {!loading && !loadError && (
            <div style={{
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              display: 'flex',
              minHeight: 'calc(100vh - 64px - 56px)',
            }}>

              {/* ── Left: tree ──────────────────────────────── */}
              <div style={{
                width: 320,
                flexShrink: 0,
                borderRight: '1px solid #E5E7EB',
                display: 'flex',
                flexDirection: 'column',
                background: '#FAFAFA',
              }}>
                <div style={{ padding: '14px 16px', borderBottom: '1px solid #E5E7EB' }}>
                  <Text style={{
                    fontSize: 11, fontWeight: 700, color: '#6B7280',
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                  }}>
                    Condition categories
                  </Text>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px 16px 8px' }}>
                  {treeData.length === 0 ? (
                    <div style={{ padding: '40px 16px', textAlign: 'center' }}>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        No groups found.
                      </Text>
                    </div>
                  ) : (
                    <Tree
                      treeData={treeData}
                      defaultExpandedKeys={defaultExpandedKeys}
                      selectedKeys={selectedGroupTitle ? [selectedGroupTitle] : []}
                      onSelect={handleTreeSelect}
                      blockNode
                    />
                  )}
                </div>
                <div style={{ padding: '10px 16px', borderTop: '1px solid #E5E7EB', background: 'white' }}>
                  <Text style={{ fontSize: 11, color: '#9CA3AF' }}>
                    {groupTitles.length} groups, {entries.length} entries total
                  </Text>
                </div>
              </div>

              {/* ── Right: entries for selected group ─────── */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}>
                {!selectedGroupTitle ? (
                  <div style={{ padding: '80px 0', textAlign: 'center' }}>
                    <Empty description="Select a category on the left to view its entries." />
                  </div>
                ) : (
                  <>
                    {/* Group title row */}
                    <div style={{
                      padding: '20px 28px 12px 28px',
                      borderBottom: '1px solid #E5E7EB',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 16,
                    }}>
                      <div style={{ overflow: 'hidden' }}>
                        <Text style={{
                          fontSize: 11, fontWeight: 700, color: '#9CA3AF',
                          letterSpacing: '0.08em', textTransform: 'uppercase',
                        }}>
                          Category
                        </Text>
                        <Title level={4} style={{ margin: '2px 0 0 0', color: '#111827' }}>
                          {selectedGroupTitle}
                        </Title>
                        <Text style={{ fontSize: 12, color: '#6B7280' }}>
                          {entriesForSelectedGroup.length} {entriesForSelectedGroup.length === 1 ? 'entry' : 'entries'}
                        </Text>
                      </div>

                      <Tooltip title={editMode ? '' : 'Unlock to add a new entry'}>
                        <Button
                          icon={<PlusOutlined />}
                          onClick={handleAddEntry}
                          disabled={!editMode}
                          type="primary"
                          style={{
                            backgroundColor: editMode ? '#162349' : undefined,
                            borderColor: editMode ? '#162349' : undefined,
                            fontWeight: 600,
                          }}
                        >
                          Add entry
                        </Button>
                      </Tooltip>
                    </div>

                    {/* Entries list */}
                    <div style={{
                      flex: 1,
                      overflowY: 'auto',
                      padding: '16px 28px 28px 28px',
                    }}>
                      {entriesForSelectedGroup.length === 0 ? (
                        <div style={{ padding: '60px 0', textAlign: 'center' }}>
                          <Empty description={
                            editMode
                              ? 'No entries in this category yet. Click "Add entry" to start.'
                              : 'No entries in this category.'
                          } />
                        </div>
                      ) : (
                        entriesForSelectedGroup.map(entry => (
                          <EntryCard
                            key={entry._id}
                            entry={entry}
                            editMode={editMode}
                            knownCategories={knownCategories}
                            onChange={(patch) => updateEntry(entry._id, patch)}
                            onDelete={() => handleDeleteEntry(entry._id)}
                          />
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

        </Content>
      </Layout>
    </Layout>
  );
};

// ────────────────────────────────────────────────────────────────────
// EntryCard — one entry block (code, category, description, terms).
// Renders read-only or editable depending on `editMode`.
// ────────────────────────────────────────────────────────────────────
const EntryCard = ({ entry, editMode, knownCategories, onChange, onDelete }) => {
  return (
    <div style={{
      border: '1px solid #E5E7EB',
      borderRadius: 8,
      padding: '16px 20px',
      marginBottom: 14,
      background: '#FFFFFF',
      transition: 'box-shadow 0.12s, border-color 0.12s',
    }}>
      {/* Top row: code + category + delete */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 12,
      }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
          {editMode ? (
            <Input
              value={entry.code}
              onChange={e => onChange({ code: e.target.value })}
              placeholder="ICD-10 code"
              style={{
                width: 160,
                fontFamily: 'monospace',
                fontWeight: 700,
                fontSize: 14,
              }}
            />
          ) : (
            entry.code ? (
              <Text style={{
                fontFamily: 'monospace',
                fontWeight: 700,
                fontSize: 15,
                color: '#162349',
                padding: '2px 8px',
                background: '#EEF2FF',
                borderRadius: 4,
                border: '1px solid #C7D2FE',
              }}>
                {entry.code}
              </Text>
            ) : (
              <Text style={{
                fontFamily: 'monospace',
                fontSize: 13,
                color: '#9CA3AF',
                fontStyle: 'italic',
              }}>
                (no code)
              </Text>
            )
          )}

          {editMode ? (
            <Select
              value={entry.category || undefined}
              onChange={(val) => onChange({ category: val || '' })}
              showSearch
              allowClear
              placeholder="category"
              style={{ width: 200 }}
              options={knownCategories.map(c => ({ value: c, label: c }))}
              // Allow free-text entry (combobox-like) via onBlur
              onBlur={(e) => {
                const v = e.target.value;
                if (v && !entry.category) onChange({ category: v });
              }}
            />
          ) : (
            entry.category ? (
              <Tag style={{
                fontSize: 12,
                color: '#374151',
                background: '#F3F4F6',
                border: '1px solid #E5E7EB',
              }}>
                {entry.category}
              </Tag>
            ) : null
          )}
        </div>

        {editMode && (
          <Popconfirm
            title="Delete this entry?"
            description={entry.description
              ? `Remove "${entry.description}"?`
              : 'Remove this entry?'}
            onConfirm={onDelete}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              size="small"
              type="text"
              icon={<DeleteOutlined style={{ color: '#DC2626' }} />}
            />
          </Popconfirm>
        )}
      </div>

      {/* Description */}
      <div style={{ marginBottom: 14 }}>
        <Text style={{
          fontSize: 10, fontWeight: 700, color: '#9CA3AF',
          letterSpacing: '0.08em', textTransform: 'uppercase',
          display: 'block', marginBottom: 4,
        }}>
          Description
        </Text>
        {editMode ? (
          <TextArea
            value={entry.description}
            onChange={e => onChange({ description: e.target.value })}
            placeholder="Official ICD-10-CM description"
            rows={2}
          />
        ) : (
          <Text style={{ fontSize: 14, color: '#1F2937' }}>
            {entry.description || (
              <span style={{ color: '#D1D5DB', fontStyle: 'italic' }}>(no description)</span>
            )}
          </Text>
        )}
      </div>

      {/* Terms */}
      <div>
        <Text style={{
          fontSize: 10, fontWeight: 700, color: '#9CA3AF',
          letterSpacing: '0.08em', textTransform: 'uppercase',
          display: 'block', marginBottom: 6,
        }}>
          Terms ({(entry.terms || []).length})
        </Text>
        {editMode ? (
          <Select
            mode="tags"
            value={entry.terms || []}
            onChange={(vals) => onChange({ terms: vals })}
            tokenSeparators={[',']}
            style={{ width: '100%' }}
            placeholder="Type and press Enter to add a synonym"
            open={false}  // Tags-mode dropdown is just noise here
          />
        ) : (
          (entry.terms || []).length === 0 ? (
            <Text style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>
              No terms defined.
            </Text>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(entry.terms || []).map((t, idx) => (
                <span key={idx} style={{
                  display: 'inline-block',
                  padding: '3px 10px',
                  borderRadius: 12,
                  fontSize: 12,
                  background: '#EFF6FF',
                  color: '#1E40AF',
                  border: '1px solid #BFDBFE',
                }}>
                  {t}
                </span>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default FocusedConditionsConfig;